/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import api from '@/lib/api';

/**
 * antiFraud.js — mirror client-side di backend/validation.py.
 * Validazione formale immediata (nessuna chiamata di rete) per feedback live nei form,
 * più i client per gli endpoint /api/validation (analisi completa e area admin).
 */

// ------------------------------------------------------------------ Codice Fiscale
const CF_ODD = { 0: 1, 1: 0, 2: 5, 3: 7, 4: 9, 5: 13, 6: 15, 7: 17, 8: 19, 9: 21, A: 1, B: 0, C: 5, D: 7, E: 9, F: 13, G: 15, H: 17, I: 19, J: 21, K: 2, L: 4, M: 18, N: 20, O: 11, P: 3, Q: 6, R: 8, S: 12, T: 14, U: 16, V: 10, W: 22, X: 25, Y: 24, Z: 23 };
const CF_EVEN = (c) => (/\d/.test(c) ? Number(c) : c.charCodeAt(0) - 65);
const CF_OMO = { L: '0', M: '1', N: '2', P: '3', Q: '4', R: '5', S: '6', T: '7', U: '8', V: '9' };
const CF_RE = /^[A-Z]{6}[0-9LMNPQRSTUV]{2}[ABCDEHLMPRST][0-9LMNPQRSTUV]{2}[A-Z][0-9LMNPQRSTUV]{3}[A-Z]$/;

export const cfControlChar = (cf15) => {
  let total = 0;
  for (let i = 0; i < 15; i++) total += i % 2 === 0 ? CF_ODD[cf15[i]] : CF_EVEN(cf15[i]);
  return String.fromCharCode(65 + (total % 26));
};

export const validateCodiceFiscale = (raw) => {
  const cf = (raw || '').replace(/\s+/g, '').toUpperCase();
  if (!cf) return { valid: false, normalized: cf, reason: 'Codice fiscale mancante' };
  if (cf.length === 11 && /^\d{11}$/.test(cf)) { const r = validatePartitaIva(cf); return { valid: r.valid, normalized: cf, kind: 'ente', reason: r.valid ? null : 'Checksum CF numerico (ente) non valido' }; }
  if (cf.length !== 16) return { valid: false, normalized: cf, kind: 'persona', reason: 'Lunghezza non valida (attesi 16 caratteri)' };
  if (!CF_RE.test(cf)) return { valid: false, normalized: cf, kind: 'persona', reason: 'Formato non valido (cognome/nome/data/comune)' };
  const dayS = [...cf.slice(9, 11)].map((c) => CF_OMO[c] ?? c).join('');
  let day = Number(dayS); if (day > 40) day -= 40;
  if (day < 1 || day > 31) return { valid: false, normalized: cf, kind: 'persona', reason: 'Giorno di nascita non plausibile' };
  const expected = cfControlChar(cf.slice(0, 15));
  if (expected !== cf[15]) return { valid: false, normalized: cf, kind: 'persona', reason: `Carattere di controllo errato (atteso ${expected})` };
  return { valid: true, normalized: cf, kind: 'persona', reason: null, sex: Number(dayS) > 40 ? 'F' : 'M' };
};

// ------------------------------------------------------------------ Partita IVA
export const validatePartitaIva = (raw) => {
  let v = (raw || '').replace(/\s+/g, '').toUpperCase();
  if (v.startsWith('IT')) v = v.slice(2);
  if (!v) return { valid: false, normalized: v, reason: 'Partita IVA mancante' };
  if (!/^\d{11}$/.test(v)) return { valid: false, normalized: v, reason: 'Deve avere 11 cifre' };
  if (v === '00000000000') return { valid: false, normalized: v, reason: 'Valore nullo non ammesso' };
  let total = 0;
  for (let i = 0; i < 10; i++) { let d = Number(v[i]); if (i % 2 === 0) total += d; else { d *= 2; total += d > 9 ? d - 9 : d; } }
  const check = (10 - (total % 10)) % 10;
  if (check !== Number(v[10])) return { valid: false, normalized: v, reason: `Cifra di controllo errata (attesa ${check})` };
  const office = Number(v.slice(7, 10));
  if (!((office >= 1 && office <= 100) || [120, 121, 888, 999].includes(office))) return { valid: false, normalized: v, reason: 'Codice ufficio provinciale non valido' };
  return { valid: true, normalized: v, reason: null };
};

// ------------------------------------------------------------------ Email / testo (euristiche leggere)
const DISPOSABLE = ['mailinator.com', 'yopmail.com', 'guerrillamail.com', '10minutemail.com', 'tempmail.com', 'temp-mail.org', 'trashmail.com', 'sharklasers.com', 'getnada.com', 'dispostable.com', 'maildrop.cc', 'throwawaymail.com', 'fakeinbox.com', 'mohmal.com', 'emailondeck.com', 'tempail.com', 'mintemail.com', 'burnermail.io', 'mailnesia.com', '1secmail.com', 'dropmail.me'];
const TEST_DOMAINS = ['example.com', 'example.org', 'example.net', 'test.com', 'test.it', 'localhost', 'fake.com'];
const TEST_KEYWORDS = ['test', 'prova', 'bug', 'placeholder', 'fake', 'dummy', 'lorem', 'ipsum', 'asdf', 'qwerty', 'debug', 'tmp', 'temp', 'xxx', 'aaaa', 'zzz', 'foo', 'bar', 'baz'];

export const validateEmail = (raw) => {
  const e = (raw || '').trim().toLowerCase();
  const at = e.lastIndexOf('@');
  if (!e || at < 1) return { valid: false, disposable: false, testDomain: false, reason: 'Email non valida' };
  const domain = e.slice(at + 1);
  const disposable = DISPOSABLE.includes(domain) || /(tempmail|temp-mail|10minute|trash|throwaway|guerrilla|disposable|burner)/.test(domain);
  const testDomain = TEST_DOMAINS.includes(domain) || /\.(test|example|invalid|localhost)$/.test(domain);
  const ok = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/.test(e);
  return { valid: ok && !disposable && !testDomain, disposable, testDomain, reason: !ok ? 'Formato email non valido' : disposable ? 'Dominio email temporaneo (disposable)' : testDomain ? 'Dominio riservato ai test' : null };
};

export const analyzeText = (text) => {
  const t = (text || '').toLowerCase();
  const flags = [];
  if (!t.trim()) return { score: 0, flags };
  const kw = TEST_KEYWORDS.find((k) => new RegExp(`(^|[^a-z])${k}([^a-z]|\\d|$)`).test(t));
  if (kw) flags.push(`keyword:${kw}`);
  if (/(.)\1{3,}/.test(t.replace(/[^a-z0-9]/g, ''))) flags.push('repeated_chars');
  const alpha = t.replace(/[^a-z]/g, '');
  if (alpha.length >= 5) { const vr = [...alpha].filter((c) => 'aeiouy'.includes(c)).length / alpha.length; if (vr < 0.2 || vr > 0.85) flags.push('unpronounceable'); }
  return { score: Math.min(100, flags.length * 30), flags };
};

// ------------------------------------------------------------------ API
export const analyzeProfileRemote = (body) => api.post('/validation/analyze', body).then((r) => r.data);
export const getFraudReport = (params) => api.get('/validation/admin/report', { params }).then((r) => r.data);
export const runFraudScan = (onlyUnchecked = false) => api.post('/validation/admin/scan', null, { params: { only_unchecked: onlyUnchecked } }).then((r) => r.data);
export const getFraudUser = (id) => api.get(`/validation/admin/users/${id}`).then((r) => r.data);
export const setFraudStatus = (id, status, note) => api.post(`/validation/admin/users/${id}/status`, { status, note }).then((r) => r.data);
/** P1 — eliminazione in blocco degli account in quarantena (richiede confirm === 'ELIMINA'). */
export const purgeQuarantine = ({ confirm, statuses, userIds, dryRun = false } = {}) =>
  api.post('/validation/admin/quarantine/purge', { confirm: confirm || '', statuses: statuses || ['SUSPECTED', 'TEST_ACCOUNT'], user_ids: userIds || null, dry_run: dryRun }).then((r) => r.data);
export const PURGE_CONFIRM_WORD = 'ELIMINA';

export const STATUS_META = {
  VERIFIED: { label: 'Verificato', variant: 'success' },
  SUSPECTED: { label: 'Sospetto', variant: 'outline' },
  TEST_ACCOUNT: { label: 'Account di test', variant: 'destructive' },
  UNCHECKED: { label: 'Non valutato', variant: 'secondary' },
};

export const FLAG_LABEL = (f) => {
  const [field, kind, val] = f.split(':');
  const F = { email: 'Email', name: 'Nome', bio: 'Bio', specialty: 'Specialità', consultation_bio: 'Bio consulto', cf: 'Codice fiscale', piva: 'P.IVA' }[field] || field;
  const K = { disposable: 'dominio temporaneo', test_domain: 'dominio di test', test_pattern: 'pattern di test', timestamp_suffix: 'suffisso numerico da script', toy_domain: 'dominio fittizio', keyword: `parola “${val}”`, keyboard: `sequenza tastiera “${val}”`, repeated_chars: 'caratteri ripetuti', unpronounceable: 'stringa casuale', consonant_run: 'consonanti consecutive', contains_digits: 'contiene cifre', too_short: 'troppo corto', invalid_checksum: 'checksum errato' }[kind] || kind;
  return `${F}: ${K}`;
};
